import styled from "@emotion/styled";

import type { McpProvider } from "./mcpProviders";

type Props = {
  provider: McpProvider;
  /**
   * Renders the server URL between the intro and the steps, which the steps refer to as "the server
   * URL above". Off by default: the credentials page lists the URL among its connection details,
   * with a copy button, so the guidance needn't repeat it.
   */
  showServerUrl?: boolean;
};

/**
 * A provider's written setup guidance — intro, sections, and when it was last checked.
 *
 * Shared by the integrations tile modal and the MCP credentials page so the same guidance reads the
 * same in both. Neither the modal's title nor the page's own chrome belongs here.
 */
export default function McpProviderInstructions({
  provider,
  showServerUrl = false,
}: Props): JSX.Element {
  return (
    <>
      {provider.intro?.map((paragraph) => <Intro key={paragraph}>{paragraph}</Intro>)}
      {/* Above the steps, so anyone who already knows how to add a connector can grab the URL
          without scrolling. The steps refer to it as the server URL above. */}
      {showServerUrl && provider.serverUrl && (
        <>
          <Subtitle>Stotles server URL</Subtitle>
          <ServerUrl>{provider.serverUrl}</ServerUrl>
        </>
      )}
      {/* Index keys: MCP_PROVIDERS is a static literal, and a block's subtitle is optional. */}
      {provider.contentBlocks.map((block, index) => (
        <div key={index}>
          {block.subtitle && <Subtitle>{block.subtitle}</Subtitle>}
          {/* Text introducing a list hugs it, rather than sitting a full gap above. */}
          {block.text && <TextBlock tight={!!block.steps}>{block.text}</TextBlock>}
          {block.steps && (
            <InstructionSteps>
              {block.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </InstructionSteps>
          )}
        </div>
      ))}
      {provider.lastVerified && <LastVerified>Last verified: {provider.lastVerified}</LastVerified>}
    </>
  );
}

const Intro = styled.p(({ theme }) => ({
  fontSize: 14,
  lineHeight: "20px",
  color: theme.colors.sysTextSecondary,
  marginBottom: 16,
}));

// Exported, with `TextBlock`, for the same reason as `InstructionSteps`: the credentials page's
// connection details section is written in the same voice as the guidance around it.
export const Subtitle = styled.h2(({ theme }) => ({
  fontSize: 16,
  lineHeight: "24px",
  fontWeight: 600,
  color: theme.colors.sysTextDefault,
  marginBottom: 8,
}));

export const TextBlock = styled.p<{ tight?: boolean }>(({ theme, tight }) => ({
  fontSize: 14,
  lineHeight: "20px",
  color: theme.colors.sysTextSecondary,
  marginBottom: tight ? 8 : 24,
}));

/**
 * Numbered setup steps. Exported because the credentials page lists its own steps above a
 * provider's, and the two are the same kind of thing — they should not drift apart.
 */
export const InstructionSteps = styled.ol(({ theme }) => ({
  fontSize: 14,
  lineHeight: "20px",
  color: theme.colors.sysTextSecondary,
  listStyle: "decimal",
  paddingLeft: 20,
  marginBottom: 24,
  "& li": {
    marginBottom: 8,
  },
}));

// Selectable rather than copy-on-click for now — see APP-6386.
const ServerUrl = styled.code(({ theme }) => ({
  display: "block",
  fontFamily: "monospace",
  fontSize: 14,
  lineHeight: "20px",
  wordBreak: "break-all",
  padding: "8px 12px",
  borderRadius: 4,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  backgroundColor: theme.colors.sysBackgroundAlternative,
  color: theme.colors.sysTextDefault,
  marginBottom: 24,
}));

const LastVerified = styled.p(({ theme }) => ({
  fontSize: 12,
  lineHeight: "16px",
  fontStyle: "italic",
  color: theme.colors.sysTextSecondary,
}));
